/**
 * Cross-platform token storage.
 *
 * • Web  → reads / clears the httpOnly-style jwtToken cookie (server-set).
 *          Writing is left to the server via Set-Cookie; we only read & clear.
 *
 * • Native (Android / iOS) → uses @capacitor/preferences (secure key-value
 *   store). Call `setNativeToken` after a successful login response when the
 *   server switches to Bearer-token auth for native clients.
 *
 * Usage in baseApi.ts (already wired up):
 *   prepareHeaders reads `nativeTokenCache` synchronously.
 */
import { Capacitor } from '@capacitor/core';

const TOKEN_KEY = 'jwtToken';

// ─── in-memory cache so prepareHeaders stays synchronous ────────────────────
export let nativeTokenCache: string | null = null;

// Initialise cache on native startup
if (Capacitor.isNativePlatform()) {
  import('@capacitor/preferences')
    .then(({ Preferences }) => Preferences.get({ key: TOKEN_KEY }))
    .then(({ value }) => { nativeTokenCache = value; })
    .catch(() => { /* leave null */ });
}

// ─── public API ─────────────────────────────────────────────────────────────

/** Read token (async, works on both web and native). */
export const getToken = async (): Promise<string | null> => {
  if (!Capacitor.isNativePlatform()) {
    const match = document.cookie.match('(^| )jwtToken=([^;].+?)(;|$)');
    return match ? match[2] : null;
  }
  const { Preferences } = await import('@capacitor/preferences');
  const { value } = await Preferences.get({ key: TOKEN_KEY });
  nativeTokenCache = value;
  return value;
};

/** Persist a Bearer token on native (call after login when server supports it). */
export const setNativeToken = async (token: string): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;
  const { Preferences } = await import('@capacitor/preferences');
  await Preferences.set({ key: TOKEN_KEY, value: token });
  nativeTokenCache = token;
};

/**
 * Clear token on both platforms.
 * Web cookie expiry is handled by existing authSlice.clearJwtCookie();
 * this only removes the native Preferences entry (fire-and-forget).
 */
export const clearToken = (): void => {
  nativeTokenCache = null;
  if (!Capacitor.isNativePlatform()) return;
  import('@capacitor/preferences')
    .then(({ Preferences }) => Preferences.remove({ key: TOKEN_KEY }))
    .catch(() => { /* ignore */ });
};
